import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';

// Action types for the reducer
type ResearchAction =
  | { type: 'SET_RESEARCH_TYPE'; payload: string | null }
  | { type: 'SET_RESEARCH_PROMPT'; payload: string }
  | { type: 'SET_TIMEFRAME'; payload: string }
  | { type: 'SET_COUNTRY'; payload: string }
  | { type: 'SET_SOURCE_TYPE'; payload: string }
  | { type: 'SET_SIMPLE_MODE'; payload: boolean }
  | { type: 'RESET_STATE' };

// State interface - research type is now optional
export interface ResearchState {
  selectedType: string | null;
  researchPrompt: string;
  timeframe: string;
  country: string;
  sourceType: string;
  // selectedFilters removed - no longer supporting dynamic research type filters
  isSimpleMode: boolean;
}

// Initial state - research type starts as null (optional)
const initialState: ResearchState = {
  selectedType: null, // Optional - will default to "General Research" during generation
  researchPrompt: '',
  timeframe: 'none',
  country: 'none',
  sourceType: 'none',
  // selectedFilters removed
  isSimpleMode: true, // Default to Simple mode
};

// Reducer function
const researchReducer = (state: ResearchState, action: ResearchAction): ResearchState => {
  switch (action.type) {
    case 'SET_RESEARCH_TYPE':
      return {
        ...state,
        selectedType: action.payload,
      };
    case 'SET_RESEARCH_PROMPT':
      return { ...state, researchPrompt: action.payload };
    case 'SET_TIMEFRAME':
      return { ...state, timeframe: action.payload };
    case 'SET_COUNTRY':
      return { ...state, country: action.payload };
    case 'SET_SOURCE_TYPE':
      return { ...state, sourceType: action.payload };
    case 'SET_SIMPLE_MODE':
      return { ...state, isSimpleMode: action.payload };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
};

// Context interface
interface ResearchContextType {
  state: ResearchState;
  actions: {
    setResearchType: (type: string | null) => void;
    setResearchPrompt: (prompt: string) => void;
    setTimeframe: (timeframe: string) => void;
    setCountry: (country: string) => void;
    setSourceType: (type: string) => void;
    // setFilter removed - no longer supporting dynamic filters
    setSimpleMode: (isSimple: boolean) => void;
    // clearFilters removed
    resetState: () => void;
  };
}

// Create context
const ResearchContext = createContext<ResearchContextType | undefined>(undefined);

// Provider component
export const ResearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(researchReducer, initialState);

  const actions = {
    setResearchType: useCallback((type: string | null) => 
      dispatch({ type: 'SET_RESEARCH_TYPE', payload: type }), []),
    setResearchPrompt: useCallback((prompt: string) => 
      dispatch({ type: 'SET_RESEARCH_PROMPT', payload: prompt }), []),
    setTimeframe: useCallback((timeframe: string) => 
      dispatch({ type: 'SET_TIMEFRAME', payload: timeframe }), []),
    setCountry: useCallback((country: string) => 
      dispatch({ type: 'SET_COUNTRY', payload: country }), []),
    setSourceType: useCallback((type: string) => 
      dispatch({ type: 'SET_SOURCE_TYPE', payload: type }), []),
    // setFilter removed - no longer supporting dynamic filters
    setSimpleMode: useCallback((isSimple: boolean) => 
      dispatch({ type: 'SET_SIMPLE_MODE', payload: isSimple }), []),
    // clearFilters removed
    resetState: useCallback(() => 
      dispatch({ type: 'RESET_STATE' }), []),
  };

  return (
    <ResearchContext.Provider value={{ state, actions }}>
      {children}
    </ResearchContext.Provider>
  );
};

// Hook to use research context
export const useResearchContext = () => {
  const context = useContext(ResearchContext);
  if (context === undefined) {
    throw new Error('useResearchContext must be used within a ResearchProvider');
  }
  return context;
};
